import { RawDraftContentState } from 'draft-js';
import { isEqual, isNull, isUndefined } from 'lodash/fp';
import React, { useCallback, useMemo } from 'react';

import { useAppDispatch, useAppSelector } from '../../../../hooks/react-redux';
import { CalculatorField } from '../../../admin/calculator/store';
import { widthsByType } from '../../../admin/dataset-builder/preview/tablePreview/TableHeader';
import { getRiskToleranceConfigIsEmpty, getRiskToleranceIndicator } from '../../../admin/risk-tolerance/utils';
import { RiskToleranceSingleIndicator } from '../../../shared/analytics/RiskToleranceSingleIndicator';
import styles from '../../../shared/datasets/SharedStyling.module.scss';
import { Checkbox, DatasetLink, DateField, DocumentField, Dropdown, LongText, Number, PartySelect, Text, Time, ValuationPercentage, Wizard, WYSIWYGField } from '../../fields';
import { CalculatorCell } from '../../fields/CalculatorCell';
import { CurrencyAmountField } from '../../fields/CurrencyAmountField';
import { DatasetFieldType, TableDatasetFieldType } from '../../store';
import { Clause } from '../Clause';
import { AnnexInstanceField, CurrencyValue, DocumentFieldRiskOverride, getConversationMaxDate, getDocumentRiskFieldsForDatasetId, getRiskToleranceForDefinitions, isAnnexField, OpenFieldSection, SingleInstanceField, TableInstanceRiskField, toggleDocumentRiskOverride, UpdatedTableField, Valuation } from '../store';
import { isBefore } from '../../../../utils/luxon';

const getField = (
    field: SingleInstanceField | AnnexInstanceField,
    index: number,
    rowId: string,
    disabled: boolean,
    showFieldUpdated: boolean,
    showDatasetUpdated: boolean,
    showAIModified: boolean,
    showAIModifiedUserCorrected: boolean,
    includedInAnnex: boolean,
    showClause: boolean,
    isTable: boolean,
    datasetId: number,
    parentFieldId: string,
    toggleSection: (fieldSection: OpenFieldSection) => void,
    getSectionOpen: (fieldSection: OpenFieldSection) => boolean,
    modalInstance?: boolean,
    isMLCorrection?: boolean
) => {
    const defaultProps = { id: field.id!, index, disabled, rowId, showFieldUpdated, showDatasetUpdated, showClause, isTable, includedInAnnex, modalInstance, parentFieldId, showAIModified, showAIModifiedUserCorrected, isMLCorrection };
    switch (field.type) {
        case DatasetFieldType.TEXT:
            return <Text {...defaultProps} value={field.value as string} datasetId={datasetId} />;
        case DatasetFieldType.LONGTEXT:
            return <LongText {...defaultProps} value={field.value as string} datasetId={datasetId} />;
        case DatasetFieldType.DATE:
            return <DateField {...defaultProps} value={field.value as null | Date} datasetId={datasetId} />;
        case DatasetFieldType.DROPDOWN:
        case DatasetFieldType.LINKFIELD:
            return <Dropdown
                {...defaultProps}
                value={field.value as null | string[]}
                type={field.type}
                listId={field.settings.dropdownLinked!}
                isMulti={field.settings.isMultiSelect}
                entityType={field.settings.entityType}
                datasetId={datasetId}
                menuPortalTarget={document.body}
            />;
        case DatasetFieldType.PARTY:
            return <PartySelect
                {...defaultProps}
                value={field.value as null | string[]}
                datasetId={datasetId}
                menuPortalTarget={document.body}
            />;
        case DatasetFieldType.DOCUMENT:
            return <DocumentField {...defaultProps} id={field.id!} documentProperty={field.settings.documentProperty!} showClause />;
        case DatasetFieldType.NUMBER:
            return <Number {...defaultProps} value={field.value as number} datasetId={datasetId} />;
        case DatasetFieldType.CHECKBOX:
            return <Checkbox {...defaultProps} checked={field.value as boolean} datasetId={datasetId} centreField={field.settings.centreField} />;
        case DatasetFieldType.DATASET:
            return <DatasetLink {...defaultProps} value={field.value as string | null} childDatasetId={field.settings.datasetLinked!} datasetId={datasetId!} />;
        case DatasetFieldType.TIME:
            return <Time {...defaultProps} value={field.value as string[]} datasetId={datasetId} />;
        case DatasetFieldType.WIZARD:
            return <Wizard {...defaultProps} value={field.value as string[]} field={field} datasetId={datasetId} toggleSection={toggleSection} getSectionOpen={getSectionOpen} />;
        case DatasetFieldType.WYSIWYG:
            return <WYSIWYGField
                {...defaultProps}
                value={field.value as RawDraftContentState | null}
                isCollapsible={field.settings.isCollapsible}
                toolbarHidden={disabled}
                datasetId={datasetId}
                toggleSection={toggleSection}
                getSectionOpen={getSectionOpen}
            />;
        case DatasetFieldType.CALCULATOR:
            return <CalculatorCell
                {...defaultProps}
                value={field.value as CalculatorField[]}
                fieldType={field.settings.calculatorFieldType!}
                datasetId={datasetId}
                showOutputField={field.settings.showOutputField!}
                showGracePeriod={field.settings.showGracePeriod!}
                includeFreeTextOption={field.settings.includeFreeTextOption}
            />;
        case DatasetFieldType.CURRENCY_AMOUNT:
            return <CurrencyAmountField {...defaultProps} currencyValue={field.value as CurrencyValue} datasetId={datasetId} />;
        case DatasetFieldType.VALUATION_PERCENTAGE:
            return <ValuationPercentage {...defaultProps} value={field.value as Valuation[]} datasetId={datasetId} showValuationPercentageMargin={field.settings.showValuationPercentageMargin} />;
        default:
            return <div className={styles.unknownFieldType}>Field type unknown.</div>;
    }
};

interface TableCellProps {
    field: SingleInstanceField | AnnexInstanceField;
    index: number;
    isEditing: boolean;
    isUpdating: boolean;
    fieldsUpdated: UpdatedTableField[];
    rowId: string;
    annexFieldIds: string[];
    isAnnexInstance: boolean;
    datasetId: number;
    parentFieldId: string;
    modalInstance?: boolean;
    toggleSection: (fieldSection: OpenFieldSection) => void;
    getSectionOpen: (fieldSection: OpenFieldSection) => boolean;
    columnCollapsed: boolean;
    rowCollapsed: boolean;
    instanceExecutedDateMatchesParent: boolean;
    isMLCorrection?: boolean;
}

export const TableCell: React.FC<TableCellProps> = ({
    field,
    index,
    isEditing,
    rowId,
    isUpdating,
    fieldsUpdated,
    annexFieldIds,
    isAnnexInstance,
    datasetId,
    parentFieldId,
    modalInstance,
    toggleSection,
    getSectionOpen,
    columnCollapsed,
    rowCollapsed,
    instanceExecutedDateMatchesParent,
    isMLCorrection
}) => {
    const dispatch = useAppDispatch();
    const { type, label, id, value } = field;
    const cellWidth = useMemo(() => columnCollapsed ? '16px' : `${widthsByType[field.type as TableDatasetFieldType]}px`, [columnCollapsed, field.type]);
    const fieldUpdatedByInstance = useMemo(() => !!fieldsUpdated.map(({ id, row }) => ({ id, row })).find(rowAndId => isEqual({ id: field.id!, row: rowId }, rowAndId)) && instanceExecutedDateMatchesParent, [field, fieldsUpdated, instanceExecutedDateMatchesParent, rowId]);
    const showFieldUpdated = useMemo(() => fieldUpdatedByInstance && !isUpdating && !isEditing, [fieldUpdatedByInstance, isUpdating, isEditing]);
    const newDatasetField = useMemo(() => field.settings.datasetUpdated || false, [field.settings.datasetUpdated]);
    const showDatasetUpdated = useMemo(() => newDatasetField && !isUpdating && !isEditing, [newDatasetField, isUpdating, isEditing]);
    const showAIModified = useMemo(() => !!field.settings.aiModified && ((!isUpdating && !isEditing) || !!isMLCorrection), [field.settings.aiModified, isUpdating, isEditing, isMLCorrection]);
    const showAIModifiedUserCorrected = useMemo(() => !!field.settings.aiModifiedUserCorrected && ((!isUpdating && !isEditing) || !!isMLCorrection), [field.settings.aiModifiedUserCorrected, isUpdating, isEditing, isMLCorrection]);
    const disabled = useMemo(() => !isEditing && !(isUpdating && (fieldUpdatedByInstance || newDatasetField)), [isEditing, isUpdating, fieldUpdatedByInstance, newDatasetField]);
    const includedInAnnex = useMemo(() => !isAnnexInstance && annexFieldIds.includes(field.id!), [isAnnexInstance, annexFieldIds, field.id]);

    const riskTolerance = useAppSelector(getRiskToleranceForDefinitions);
    const documentRiskFields = useAppSelector(getDocumentRiskFieldsForDatasetId(datasetId));
    const conversationMaxDate = useAppSelector(getConversationMaxDate);

    const clientRiskConversation = useMemo(() => !isUndefined(documentRiskFields) && (documentRiskFields.includedFields as TableInstanceRiskField[]).find(instanceRiskField => instanceRiskField.rowId === rowId)?.riskFields.find(({ fieldId }) => fieldId === field.id)?.clientRiskConversation || [], [documentRiskFields, field.id, rowId]);
    const relevantConversation = useMemo(() => isNull(conversationMaxDate) ? clientRiskConversation : clientRiskConversation.filter(({ documentExecutedDate }) => isBefore(documentExecutedDate, conversationMaxDate)), [conversationMaxDate, clientRiskConversation]);

    const riskToleranceForDataset = useMemo(() => riskTolerance.find(riskTolerance => riskTolerance.datasetId === datasetId), [riskTolerance, datasetId]);
    const fieldRiskConfig = useMemo(() => !isUndefined(riskToleranceForDataset) && riskToleranceForDataset.config.find(({ fieldId }) => fieldId === field.id) || undefined, [riskToleranceForDataset, field.id]);
    const showRiskFieldIndicator = useMemo(() => !isUndefined(fieldRiskConfig) && !!fieldRiskConfig.includeRiskField && !getRiskToleranceConfigIsEmpty(fieldRiskConfig, field.type, field.value), [fieldRiskConfig, field.type, field.value]);
    const riskIndicator = getRiskToleranceIndicator(fieldRiskConfig, value, type, relevantConversation, true);

    const documentRiskOverride: DocumentFieldRiskOverride = useMemo(() => ({
        datasetId,
        rowId,
        sectionId: null,
        fieldId: id!,
        clientRiskConversation,
        label: label,
        fieldRiskConfig,
        value,
        fieldType: type
    }), [datasetId, rowId, id, clientRiskConversation, label, fieldRiskConfig, value, type]);

    const canOpenOverride = useMemo(() => !isMLCorrection && (relevantConversation.length > 0 || isNull(conversationMaxDate)), [isMLCorrection, relevantConversation.length, conversationMaxDate]);
    const openRiskConversationModal = useCallback(() => canOpenOverride && dispatch(toggleDocumentRiskOverride(documentRiskOverride)), [dispatch, documentRiskOverride, canOpenOverride]);

    const cellContent = useMemo(() => {
        if (rowCollapsed) {
            return (
                <div className={styles.collapsedRowPlaceholder} />
            );
        }
        if (columnCollapsed) {
            return (
                <div className={styles.collapsedPlaceholder} />
            );
        }
        return (
            <div className={styles.openCellWrapper}>
                {getField(field, index, rowId, disabled, showFieldUpdated, showDatasetUpdated, showAIModified, showAIModifiedUserCorrected, includedInAnnex, field.settings.showClause || false, true, datasetId, parentFieldId, toggleSection, getSectionOpen, modalInstance, isMLCorrection)}
                {!isAnnexField(field, isAnnexInstance) && field.settings.showClause &&
                    <Clause
                        modalOpen={field.settings.clauseOpen || false}
                        clauseLabel={field.clauseLabel}
                        testId='table-clause'
                        isEditing={!isEditing}
                        index={index}
                        rowId={rowId}
                        datasetId={datasetId}
                        modalInstance={modalInstance}
                        parentFieldId={parentFieldId}
                    />}
                {showRiskFieldIndicator && <RiskToleranceSingleIndicator riskIndicator={riskIndicator} fontSize={22} onClick={openRiskConversationModal} canClick={canOpenOverride} />}
            </div>
        );
    }, [rowCollapsed, columnCollapsed, field, index, rowId, disabled, showFieldUpdated, showDatasetUpdated, showAIModified, showAIModifiedUserCorrected, includedInAnnex, datasetId, parentFieldId, toggleSection, getSectionOpen, modalInstance, isMLCorrection, isAnnexInstance, isEditing, showRiskFieldIndicator, openRiskConversationModal, riskIndicator, canOpenOverride]);

    return (
        <div
            className={styles.cellWrapper}
            style={{ width: cellWidth, minWidth: cellWidth }}
            data-testid={`table-cell-${index}-wrapper`}
        >
            {cellContent}
        </div>
    );
};
